import { touchFloor } from './_touchTarget.js'

// AssistantModeChip — WHAT the AI makes, switched from the composer itself
// (AI_ASSISTANT_SPEC §1, §8, §12.1 and Nika's pick 5B, 2026-09-28: "compact chip
// opens the mode menu"; 2026-09-29: "dropdown should contain [Assistant,
// Dashboard, Tools (platform apps), Apps (frontend), Presentation]").
//
// Six modes, one per-user choice: Assistant (a conversation whose answers
// carry live blocks), Dashboard (a board of widgets), Tools (work inside the
// installed platform apps), Apps (a frontend app built in this workspace),
// Presentation (a live deck), Fly (every ask composes a page — Nika
// 2026-10-17). The chip names the current mode with a caret
// ("Assistant ▾") and opens the kit menu (MenuPanel rows, the current one
// checked) in the shell dropdown layer; a row writes through the ONE writer,
// `setAssistantMode(mode)` (the assistant lane's, workspace
// functions/assistantMode.js), which persists `assistantMode` per user. The
// chip names `root.assistantMode` — except while a thread holds the screen
// (the main thread, or the mount that IS one: the drawer's `threadSurface`),
// when it names that view (`modeChipShown`, below).
//
// Offered only where that writer exists (another app mounting the composer has
// none); it hides itself, so a mount needs no gate — a mount-site hide would
// REPLACE this one. In the composer's small pill (its state `size`) it is the
// COMPACT chip — the glyph + caret, like the phone form (design-d2's 390
// measure: 39.4 × 36), its name still "Mode: <mode>" — so the pill always
// offers the mode switch (the live pass on deploy 8 found it absent there).
//
// A real <button>: the mode as its name, aria-haspopup="menu" and
// aria-expanded while its menu is open, ArrowDown / ArrowUp open the menu (the
// menu-button contract; Enter and Space already click). The row geometry is
// the composer's model pill's.
//
// It never shrinks: squeezed by a fixed-width row it lost its caret — the menu
// sign — and its word overflowed the box (live on dev 3691a9c28 at 390). On
// phones (the mobileL band, the composer's own phone step) it shows the mode's
// glyph — the one its menu row paints — and the caret, never the word; its
// name keeps the word.
//
// THE MERGED FORM — CONTEXT + MODE IN ONE TRIGGER (Nika 2026-10-07: "merge two
// dashboard and context switcher dropdowns into one in aipropttextarea"). A
// mount that sets the flat `withContext: true` (the composer's two mounts —
// never the drawer header) gets ONE dropdown for both jobs, where the shell's
// context readers exist (`aiScopeOptions`, `aiScopeCurrent`, `setAiScope` —
// workspace functions/aiScope.js):
//   • the trigger: the mode's glyph plate, the CONTEXT's short name (capped at
//     D2 with an ellipsis, its full name in the title), the caret; its name is
//     "Mode: <mode> · Context: <context>";
//   • the menu: a "Context" group first (the context rows, most specific
//     first, the chosen one checked), then a "Mode" group (the five modes).
//     A context row writes through `setAiScope(key)` — the focus panel's
//     switch's own writer — and a mode row through `setAssistantMode(mode)`.
//     The captions are aria-hidden spans, never rows: the house menu keyboard
//     walks the nine rows straight through.
// Without those readers the merged mount is the mode chip it always was.
// The context pill that sat beside it (AiScopePill) is gone.
export const AssistantModeChip = {
  tag: 'button',
  type: 'button',

  // FA204 / frank: every helper lives in scope (a module-scope read from a
  // serialized prop factory reads as undefined once this is frank.toJSON'd).
  scope: {
    // Each mode names its polyglot key LITERALLY (the i18n gate forbids a key
    // built by concatenation — a built key is invisible to the extraction).
    // `icon` is the glyph painted today — five DIFFERENT shapes, because on
    // phones the chip shows the glyph alone (a spark, a layout, a stack, the
    // code brackets, play); `glyph` is the design lane's line glyph for the
    // mode (dropdowns spec §3.3, `ln*`), which replaces `icon` once that icon
    // set is in the sprite.
    modeChipModes() {
      // A comm host (workspace docs/COMM_HOST.md) offers its own modes only —
      // commProfileData.js commAssistantModes(), restated (scope methods are
      // serialized alone).
      let comm = false
      try {
        const host = String((window.location && window.location.hostname) || '').toLowerCase()
        comm =
          /(^|\.)comm?\.symbols\.app$/.test(host) ||
          window.localStorage.getItem('symbols_force_comm') === '1' ||
          (window.location.search || '').includes('comm=1')
      } catch (_) {
        comm = false
      }
      const all = [
        {
          mode: 'assistant',
          key: 'ai.assistantMode.assistant',
          icon: 'magicstarOutline',
          glyph: 'lnAssistant'
        },
        {
          mode: 'dashboard',
          key: 'ai.assistantMode.dashboard',
          icon: 'layoutOutline',
          glyph: 'lnDashboard'
        },
        { mode: 'tools', key: 'ai.assistantMode.tools', icon: 'layers', glyph: 'lnTools' },
        { mode: 'apps', key: 'ai.assistantMode.apps', icon: 'code', glyph: 'lnApps' },
        {
          mode: 'presentation',
          key: 'ai.assistantMode.presentation',
          icon: 'play',
          glyph: 'lnPresentation'
        },
        // Fly (Nika 2026-10-17): every ask composes a page — the wing of the
        // Fly mark: the swoosh as PIXEL art (designSystem/icons.js pxFly — Nika
        // 2026-10-18, "choose z_current but pixelated"); it shows only at 16 px.
        { mode: 'fly', key: 'ai.assistantMode.fly', icon: 'send', glyph: 'pxFly' }
      ]
      return comm ? all.filter((x) => ['assistant', 'tools', 'fly'].includes(x.mode)) : all
    },
    modeChipKey(mode) {
      const m = this.modeChipModes().find((x) => x.mode === mode)
      return m ? m.key : 'ai.assistantMode.assistant'
    },
    // the mode's own glyph — the one its menu row paints: the design lane's LINE glyph (dropdowns spec
    // §7.3 B1; the workspace glyph set has been in the sprite since 217ecd70a) — except Fly, whose mark is
    // the PIXEL swoosh everywhere it shows (Nika 2026-10-18 "choose z_current but pixelated": pxFly)
    modeChipIcon(mode) {
      const m = this.modeChipModes().find((x) => x.mode === mode)
      return m ? m.glyph : 'lnAssistant'
    },
    // WHAT THE CHIP NAMES — its word, glyph and name: { key, glyph }. The
    // stored mode, except while a thread holds the screen: then the VIEW there
    // (plan rule 2.1-1 "labels read the view"; item 4 = A, the concierge for
    // Nika 2026-10-28: "one rule everywhere") — "Chat". The main thread's view
    // is read from the one reader the thread head, the placeholder and the Ask
    // menu read (workspace assistantAskRoute.js assistantEffectiveView; Apps
    // mode's thread is Build, which the stored Apps already names). A mount
    // that IS a thread surface says so with the flat `threadSurface` (the
    // sidebar drawer's head: shown only with its thread, and an ask typed
    // there stays in that chat) — Chat by the same rule, Apps keeps its name.
    // The stored mode stays — nothing is written, the menu still checks it —
    // and the chip names it again once the thread closes. A host without the
    // readers (canvas standalone) names the stored mode.
    modeChipShown(el, root) {
      const mode = this.modeChipCurrent(root)
      const chat = { key: 'workspace.homeView.chat', glyph: 'lnChat' }
      if (el && el.threadSurface && mode !== 'apps') return chat
      const fns = el && el.context && el.context.functions
      const thread =
        !!fns &&
        typeof fns.assistantMainThreadOpen === 'function' &&
        typeof fns.assistantEffectiveView === 'function' &&
        !!el.call('assistantMainThreadOpen', root)
      if (thread && el.call('assistantEffectiveView', root) === 'chat') return chat
      return { key: this.modeChipKey(mode), glyph: this.modeChipIcon(mode) }
    },
    modeChipCurrent(root) {
      const m = root && root.assistantMode
      return this.modeChipModes().some((x) => x.mode === m) ? m : 'assistant'
    },
    modeChipOffered(el, s) {
      const fns = el && el.context && el.context.functions
      return !!fns && typeof fns.setAssistantMode === 'function'
    },
    // The merged form is on: the mount asked for it (`withContext`, read off
    // the CHIP element) and the shell's context readers + writer exist.
    modeChipContextOn(chip) {
      if (!chip || !chip.withContext) return false
      const fns = chip.context && chip.context.functions
      return (
        !!fns &&
        typeof fns.aiScopeOptions === 'function' &&
        typeof fns.aiScopeCurrent === 'function' &&
        typeof fns.setAiScope === 'function'
      )
    },
    // The chosen (or default) context row, or null where the form is off.
    modeChipContextRow(chip, s) {
      if (!this.modeChipContextOn(chip)) return null
      try {
        return chip.call('aiScopeCurrent', s && s.root) || null
      } catch (_) {
        return null
      }
    },
    // The menu's context rows: every option, the chosen one checked. A row
    // reads its option key off ITSELF (`scopeKey`), never a captured variable
    // (frank FA205). Its key is prefixed: a data key that names an HTML tag
    // becomes that tag.
    modeChipContextRows(chip, s) {
      const root = s && s.root
      let options = []
      try {
        options = chip.call('aiScopeOptions', root) || []
      } catch (_) {
        options = []
      }
      const current = this.modeChipContextRow(chip, s)
      const chosen = current ? current.key : null
      return options.map((o) => ({
        key: 'ctx-' + o.key,
        scopeKey: o.key,
        glyph: o.glyph || 'lnApps',
        text: o.label || o.short || '',
        checked: o.key === chosen,
        onClick: (ev, row) => {
          row.call('closeDropdown', 'assistantMode')
          row.call('setAiScope', row.scopeKey)
        }
      }))
    },
    // One labelled group of the merged menu: role="group" named by its
    // caption; the caption itself is aria-hidden (the group's name already
    // says it) and is a span, so the menu keyboard never lands on it.
    modeChipGroup(label, rows) {
      return {
        role: 'group',
        ariaLabel: label,
        flow: 'y',
        gap: '0',
        minWidth: '0',
        AiPanelLabel: { ariaHidden: 'true', text: label, padding: 'Y Z1 X' },
        GroupRows: {
          flow: 'y',
          gap: '0',
          minWidth: '0',
          childExtends: 'MenuPanelItem',
          children: rows
        }
      }
    },
    // aria-expanded follows the dropdown plugin's own layer: its onOpen once
    // the layer is up, its onClose on every close — a row, Escape, an outside
    // press, or another dropdown replacing this one. Written through the
    // element's own update as `attr` (AppScrollRow's path): the flat
    // `ariaExpanded` is applied once at create, and an update of the flat key
    // does not re-apply it (measured on the real smbls create()).
    modeChipExpanded(el, open) {
      if (!el || !el.node) return
      el.update({ attr: { 'aria-expanded': open ? 'true' : 'false' } })
    },
    modeChipOpen(el, s) {
      const current = this.modeChipCurrent(s && s.root)
      // A row's handler reads its mode off the ROW (a flat `mode` prop), never
      // a variable captured from this map — a captured one would not survive
      // serialization (frank FA205).
      const rows = this.modeChipModes().map((m) => ({
        key: m.mode,
        mode: m.mode,
        // the mode's glyph (LINE; Fly's is its pixel mark) — MenuPanelItem's own Plate slot (c24d97aa8) draws it with the house row's
        // spacing and inversion; the row carries no plate, spacing or state rule of its own (Nika: "fix ai mode
        // chooser dropdown gaps in dropdown list items" — the label touched a row-level plate, 0 px)
        glyph: m.glyph,
        text: el.call('polyglot', m.key),
        checked: m.mode === current,
        onClick: (ev, row) => {
          row.call('closeDropdown', 'assistantMode')
          row.call('setAssistantMode', row.mode)
        }
      }))
      // the body paints nothing: MenuPanel's opaque `dialog` surface and its elevation are dropped (the
      // SearchAppsMenu recipe); its keyboard model, role and focus arming stay
      const body = {
        extends: 'MenuPanel',
        theme: null,
        background: 'transparent',
        backdropFilter: 'none',
        shadow: 'none',
        ':focus-visible': { boxShadow: 'none' },
        // DESIGN_SPEC 6.5: reduced transparency = the opaque `dialog` surface (the body covers the Panel)
        '@reduceTransparency': { theme: 'dialog' }
      }
      // THE MERGED MENU (withContext): the Context group first, then the Mode group; else the flat mode menu.
      const content = this.modeChipContextOn(el)
        ? {
            ...body,
            ariaLabel: el.call('polyglot', 'ai.assistantMode.menuWithContext'),
            ContextGroup: this.modeChipGroup(
              el.call('polyglot', 'ai.scope.label'),
              this.modeChipContextRows(el, s)
            ),
            ModeGroup: this.modeChipGroup(el.call('polyglot', 'ai.assistantMode.label'), rows)
          }
        : {
            ...body,
            ariaLabel: el.call('polyglot', 'ai.assistantMode.label'),
            childExtends: 'MenuPanelItem',
            children: rows
          }
      el.call('openDropdown', {
        anchorEl: el,
        id: 'assistantMode',
        placement: 'top-start',
        round: 'radiusCard',
        // THE GLASS ON THE LAYER (dropdowns spec §7.3 B2, the 9d0573e22 lesson): the plugin Panel carries the
        // one popover glass and the elevation — its own backdrop-filter reaches the page; a glass on the body
        // inside it would not. No `background` here: a surface override beats the theme's fill.
        theme: 'search-glass',
        shadow: 'overlay',
        onOpen: () => el.scope.modeChipExpanded(el, true),
        onClose: () => el.scope.modeChipExpanded(el, false),
        content
      })
    }
  },

  // the stored mode, and the keys the main thread's reader reads
  // (workspace assistantMainThread.js assistantMainThreadOpen)
  subscribeTo: [
    'assistantMode',
    '_homeThreadOpen',
    'assistantDock',
    'currentPath',
    '_mainThreadPath'
  ],
  hide: (el, s) => !el.scope.modeChipOffered(el, s),

  flow: 'x',
  align: 'center',
  gap: 'X',
  flexShrink: '0',
  // the inverted glyph plate leads (dropdowns spec §4 capsule: inline padding `Y Z2 Y Y`)
  padding: 'Y Z2 Y Y',
  // the phone form is a glyph + caret: the inline padding drops to the block
  // one (Y), so it takes ~40 px where the word takes 101-141 px (en / ka)
  '@mobileL': { paddingInline: 'Y' },
  // the small pill's compact form: the same glyph + caret geometry — unless the
  // merged form names the context there (the pill has no second trigger now)
  class: {
    isCompact: (el, s) => !!(s && s.size === 'small') && !el.scope.modeChipContextRow(el, s)
  },
  '&.isCompact': { paddingInline: 'Y' },
  round: 'radiusControl',
  border: 'none',
  background: 'transparent',
  color: 'title',
  fontWeight: '400',
  cursor: 'pointer',
  ':hover': { background: 'hover' },
  // the open menu (dropdowns spec §7.3 B3): the `selected` wash while aria-expanded
  '&[aria-expanded="true"]': { background: 'selected' },
  // A finger's target: the composer's mode prefix measured 47-50 x 35-36 on
  // dev DFI with a touch device simulated — the 44pt floor (_touchTarget.js).
  '@coarse': { position: 'relative', ...touchFloor },
  ariaHaspopup: 'menu',
  ariaExpanded: 'false',
  ariaLabel: (el, s) => {
    const sc = el.scope
    const mode = el.call('polyglot', sc.modeChipShown(el, s && s.root).key)
    const row = sc.modeChipContextRow(el, s)
    if (!row) return el.call('polyglot', 'ai.assistantMode.label') + ': ' + mode
    return el
      .call('polyglot', 'ai.assistantMode.labelWithContext')
      .replace('{mode}', () => mode)
      .replace('{context}', () => row.label || row.short || '')
  },

  // The phone form's glyph (the Icon built-in): hidden by default, shown on
  // the mobileL band only. Glyph and caret never shrink — even where a mount
  // makes the chip elastic (the canvas composer truncates the Label first,
  // 40e9d2445), the menu sign stays whole.
  Icon: {
    name: (el, s) => el.parent.scope.modeChipShown(el.parent, s && s.root).glyph,
    // THE INVERTED GLYPH PLATE (dropdowns spec §4 "the mode chip", §7.3 B3): this glyph child IS the plate —
    // `glyphPlateXs`, round, `highlight` fill, `title-reversed` ink, the glyph inset to exactly `iconSm` (a
    // pixel glyph shows whole cells only at 16 px) — and it shows
    // on every width: plate + word + caret on desktop, plate + caret on phones and in the small pill (the word
    // hides there, the name keeps it). Glyph and caret never shrink — even where a mount makes the chip elastic
    // (the canvas composer truncates the Label first, 40e9d2445), the menu sign stays whole.
    flexShrink: '0',
    boxSizing: 'border-box',
    boxSize: 'glyphPlateXs',
    // The glyph is exactly `iconSm` inside the plate — the inset is computed
    // from the two tokens, never the em-based `X` (3.6–4.0 px by font size): a
    // PIXEL glyph (pxFly) shows whole cells only at 16 px.
    padding: 'calc((var(--size-glyphPlateXs) - var(--size-iconSm)) / 2)',
    round: 'radiusPill',
    background: 'highlight',
    color: 'title-reversed',
    // the small pill's compact marker stays on the glyph (the pill slot reads it); it styles nothing here now
    class: { isCompact: (el, s) => !!(s && s.size === 'small') }
  },
  // The word: the mode's — or, in the merged form, the CONTEXT's short name,
  // capped at D2 (the measured row budget, composerToolRowGeometry) with an
  // ellipsis and its full name in the title (the overflow law's secondary
  // meta). The mode word is never capped: the chip stays rigid for it.
  Label: {
    tag: 'span',
    text: (el, s) => {
      const sc = el.parent.scope
      const row = sc.modeChipContextRow(el.parent, s)
      if (row) return row.short || row.label || ''
      return el.call('polyglot', sc.modeChipShown(el.parent, s && s.root).key)
    },
    title: (el, s) => {
      const row = el.parent.scope.modeChipContextRow(el.parent, s)
      return (row && row.label) || null
    },
    whiteSpace: 'nowrap',
    '@mobileL': { display: 'none' },
    class: {
      isCompact: (el, s) =>
        !!(s && s.size === 'small') && !el.parent.scope.modeChipContextRow(el.parent, s),
      hasContext: (el, s) => !!el.parent.scope.modeChipContextRow(el.parent, s)
    },
    '&.isCompact': { display: 'none' },
    '&.hasContext': { maxWidth: 'D2', overflow: 'hidden', textOverflow: 'ellipsis' }
  },
  Caret: {
    extends: 'Icon',
    name: 'arrow angle down',
    flexShrink: '0',
    boxSize: 'Z',
    opacity: '.6'
  },

  onKeydown: (e, el, s) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    e.preventDefault()
    e.stopPropagation()
    el.scope.modeChipOpen(el, s)
  },
  onClick: (e, el, s) => {
    e.preventDefault()
    e.stopPropagation()
    el.scope.modeChipOpen(el, s)
  }
}
